import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@attendance/ui/utils';
import { Bell, Clock, Home, Menu, Stamp } from 'lucide-react';
import { useFeatureEnabled } from '../../stores/brandingStore.js';
import { useAuthStore } from '../../stores/authStore.js';
import { useUnreadNotificationCount } from '../../hooks/useUnreadNotifications.js';
import { UnreadBadge } from './UnreadBadge.js';

/**
 * Mobile-only bottom navigation (≤5 targets, icon + label, ≥44px). The center
 * slot is a raised primary check-in/out action; the last slot opens the full
 * drawer for everything else. Hidden on md+ where the sidebar takes over.
 *
 * The self-service slots (the "Attendance" tab → /my-attendance and the raised
 * check-in/out CTA → /punch) are hidden when the tenant runs as a management-
 * only console (selfService off), leaving Home, Alerts and Menu.
 *
 * Type size: `text-xs` (12px) is the floor the rest of the app already holds,
 * and this bar is the worst place to go under it — it is the primary navigation
 * for a foreman reading a phone in direct sun. The labels used to be 10px. The
 * bar simply got taller (icon 20 + gap + 16px line-box + padding ≈ 50px); the
 * `min-h-touch` floor on every target is unchanged, so nothing shrank to pay
 * for the legibility. Labels carry `whitespace-nowrap` so a narrow phone makes
 * the slots unequal rather than wrapping "Check in" onto two lines — flexbox
 * min-content sizing keeps every label whole down to ~320px.
 *
 * FOCUS. The one focus treatment (`focusRing`) draws in `--ring`, and `--ring`
 * is defined as the SAME triplet as `--primary` in both themes (index.css:160
 * and :353). This bar IS `bg-primary`, so the canon ring would be brand-on-
 * brand — a 1:1 outline, i.e. no indicator at all. So the bar keeps the canon's
 * GEOMETRY (2px at a 2px transparent offset, per packages/ui/src/lib/focus.ts)
 * and swaps only the hue for the paired ink, measured 7.63:1 (light) / 7.71:1
 * (dark) over the bar. Stated once, below, and used by every target on the bar
 * including the raised check-in disc.
 *
 * Colour: this bar is the mobile counterpart of the desktop brand rail — below
 * md the rail is off-canvas, so without it the brand disappears from the shell
 * exactly where the sister app keeps it strongest. Same rule as the rail: the
 * bar is `--primary` and every mark on it is `--primary-foreground` at some
 * alpha, never a literal white, because `--primary` inverts between themes and
 * is re-seeded per tenant. Measured over the bar: full opacity 7.63:1 (light) /
 * 7.71:1 (dark), the /80 resting label 5.37 / 5.54, and the active tab (ink at
 * full opacity on the /15 tint) 5.74 / 5.82.
 */
/** See the FOCUS note above: canon geometry, brand-paired ink. */
const BAR_FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-foreground';

export function MobileBottomNav({ onMenu }: { onMenu: () => void }): JSX.Element {
  const { t } = useTranslation();
  // Both conditions: the tenant offers self-service, AND this login has an
  // Employee record behind it. Bottom nav has only five slots — spending two
  // of them (plus the raised CTA) on destinations that dead-end for an
  // admin-only account is the most expensive place in the app to get this wrong.
  const hasEmployeeRecord = useAuthStore((s) => Boolean(s.user?.employeeId));
  const selfService = useFeatureEnabled('selfService') && hasEmployeeRecord;
  // The SAME number the desktop bell shows — one hook, one query key, one
  // request. A phone is where most of these users read their alerts, and this
  // bar carried no count at all while the header badge did.
  const unread = useUnreadNotificationCount();

  const tab = (active: boolean): string =>
    cn(
      'flex min-h-touch flex-1 flex-col items-center justify-center gap-0.5 whitespace-nowrap py-1.5 text-xs font-medium transition-colors duration-fast',
      BAR_FOCUS,
      active
        ? 'bg-primary-foreground/15 font-semibold text-primary-foreground'
        : 'text-primary-foreground/80'
    );

  return (
    <nav
      aria-label={t('mobileNav.label', 'Primary')}
      className="fixed inset-x-0 bottom-0 z-30 flex items-stretch border-t border-primary-foreground/15 bg-primary pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      {/* Home only gets its OWN tab on a management-only console. With
          self-service on, home IS the check-in screen, so the raised disc below
          is the home tab — two slots pointing at one page would just be the
          same destination twice on a five-slot bar. */}
      {!selfService && (
        <NavLink to="/" end className={({ isActive }) => tab(isActive)}>
          <Home size={20} aria-hidden />
          <span>{t('nav.home', 'Home')}</span>
        </NavLink>
      )}
      {selfService && (
        <NavLink to="/my-attendance" className={({ isActive }) => tab(isActive)}>
          <Clock size={20} aria-hidden />
          <span>{t('mobileNav.attendance', 'Attendance')}</span>
        </NavLink>
      )}

      {/* Center: raised check-in/out CTA — the home screen, where the punch
          button now lives. */}
      {selfService && (
        <NavLink
          to="/"
          end
          aria-label={t('nav.punch', 'Check in / out')}
          className={cn(
            'relative flex flex-1 flex-col items-center justify-end rounded-control',
            // This slot used to carry `focus-visible:outline-none` and supply
            // nothing in its place, so the app's most prominent mobile action
            // was the one target on the bar with NO keyboard indicator. The
            // `ring-4 ring-background` on the disc below is a cut-out and the
            // `border-2` is the ACTIVE state; neither tracks focus.
            BAR_FOCUS
          )}
        >
          {({ isActive }) => (
            <>
              {/* The disc STRADDLES the bar's top edge: ~40% of it sits over
                  the page background and ~60% over the bar. Two backdrops, so
                  it cannot be tinted — it stays an opaque `--primary` disc and
                  the `ring-4 ring-background` is what cuts it out of the
                  now-matching bar. The active state is an inner outline in the
                  paired ink (7.6:1), which reads on both backdrops; the old
                  `bg-primary/10 text-primary` would have been primary-on-
                  primary, i.e. 1:1, from the moment the bar became brand-
                  coloured. */}
              {/* `shadow-hover`, not the deprecated `shadow-elev-lg` alias
                  (which already resolved to it, so nothing moves visually).
                  The role reads oddly on a thing nobody hovers, and it is still
                  the right one: the four-role ramp has exactly one "raised"
                  weight, and a permanently-lifted object takes it. `Card`'s
                  `variant="elevated"` sets that precedent for the same reason.
                  Not `shadow-popover` — this disc is fixed chrome that is
                  always there, not something that arrived over the page, and
                  the popover's 50px pool on a 48px disc reads as a smudge. */}
              <span
                className={cn(
                  'absolute -top-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-hover ring-4 ring-background transition-colors duration-fast',
                  isActive && 'border-2 border-primary-foreground'
                )}
              >
                <Stamp size={22} aria-hidden />
              </span>
              <span
                className={cn(
                  'whitespace-nowrap pb-1.5 text-xs font-medium',
                  isActive
                    ? 'font-semibold text-primary-foreground'
                    : 'text-primary-foreground/80'
                )}
              >
                {t('mobileNav.checkIn', 'Check in')}
              </span>
            </>
          )}
        </NavLink>
      )}

      <NavLink to="/notifications" className={({ isActive }) => tab(isActive)}>
        {/* The badge is anchored to the ICON, not the slot: the slot is a
            flex-1 column whose width changes with the label, so a corner-pinned
            pill would sit a different distance from the bell on every phone. */}
        <span className="relative inline-flex">
          <Bell size={20} aria-hidden />
          <UnreadBadge count={unread} className="absolute -top-1.5 -end-2.5" />
        </span>
        <span>{t('mobileNav.alerts', 'Alerts')}</span>
      </NavLink>
      <button type="button" onClick={onMenu} className={tab(false)}>
        <Menu size={20} aria-hidden />
        <span>{t('mobileNav.menu', 'Menu')}</span>
      </button>
    </nav>
  );
}
